import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { merge } from 'lodash';
import ReactApexChart from 'react-apexcharts';
import { BaseOptionChart } from './charts';

const STRATEGY_KEYS = ['control_scheduled', 'control_actual', 'dumb', 'oracle', 'prediction_only'];

const STRATEGY_COLORS = {
  control_scheduled: '#2196F3',
  control_actual: '#1565C0',
  dumb: '#9E9E9E',
  oracle: '#4CAF50',
  prediction_only: '#FF9800',
};

const STRATEGY_LABELS = {
  control_scheduled: 'Scheduled',
  control_actual: 'Actual',
  dumb: 'Dumb',
  oracle: 'Oracle',
  prediction_only: 'Pred-Only',
};

export default function CostComparisonBar({ evaluations = [] }) {
  if (!evaluations.length) return null;

  // Aggregate: total cost per strategy across all sessions
  const totals = {};
  const counts = {};
  for (const key of STRATEGY_KEYS) {
    totals[key] = 0;
    counts[key] = 0;
  }

  for (const ev of evaluations) {
    const s = ev.strategies || {};
    // Fallback: old evaluations stored a single "control" key
    const normalized = {
      ...s,
      control_scheduled: s.control_scheduled || s.control || null,
      control_actual: s.control_actual || null,
    };
    for (const key of STRATEGY_KEYS) {
      if (normalized[key] && normalized[key].cost_usd != null) {
        totals[key] += normalized[key].cost_usd;
        counts[key] += 1;
      }
    }
  }

  const categories = [];
  const totalData = [];
  const avgData = [];
  const colors = [];

  for (const key of STRATEGY_KEYS) {
    if (counts[key] > 0) {
      categories.push(STRATEGY_LABELS[key]);
      totalData.push(parseFloat(totals[key].toFixed(2)));
      avgData.push(parseFloat((totals[key] / counts[key]).toFixed(2)));
      colors.push(STRATEGY_COLORS[key]);
    }
  }

  const series = [
    { name: 'Total Cost (USD)', data: totalData },
    { name: 'Avg Cost (USD)', data: avgData },
  ];

  const chartOptions = merge(BaseOptionChart(), {
    colors: ['#1976D2', '#90CAF9'],
    plotOptions: {
      bar: {
        columnWidth: '60%',
        borderRadius: 4,
      },
    },
    dataLabels: { enabled: true, formatter: (val) => `$${val.toFixed(2)}` },
    xaxis: {
      categories,
      labels: { style: { fontSize: '12px' } },
    },
    yaxis: {
      title: { text: 'Cost (USD)' },
      labels: { formatter: (val) => `$${val.toFixed(2)}` },
    },
    tooltip: {
      y: { formatter: (val) => `$${val.toFixed(2)}` },
    },
    legend: { show: true, position: 'top' },
  });

  const sessionCount = evaluations.length;
  const controlTotal = totals.control_actual || totals.control_scheduled || 0;
  const dumbTotal = totals.dumb || 0;
  const oracleTotal = totals.oracle || 0;
  const savingsVsDumb = dumbTotal - controlTotal;
  const gapToOracle = controlTotal - oracleTotal;

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          Strategy Cost Comparison — {sessionCount} session{sessionCount !== 1 ? 's' : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Savings vs Dumb: ${savingsVsDumb.toFixed(2)} | Gap to Oracle: ${gapToOracle.toFixed(2)}
        </Typography>
        <ReactApexChart type="bar" series={series} options={chartOptions} height={280} />
      </CardContent>
    </Card>
  );
}
